import React from 'react';
import {
  Box,
  Flex,
  Button,
  Stack,
  useColorModeValue,
  Container,
  IconButton,
  Drawer,
  DrawerBody,
  DrawerHeader,
  DrawerOverlay,
  DrawerContent,
  DrawerCloseButton,
  useDisclosure,
  VStack,
  Image,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
} from '@chakra-ui/react';
import { HamburgerIcon, ChevronDownIcon } from '@chakra-ui/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';

const NavLink = ({ to, children, onClick }) => {
  const location = useLocation();
  const isActive = location.pathname === to;
  
  return (
    <Button
      variant="ghost"
      fontSize="md"
      fontWeight={isActive ? 'bold' : 'medium'}
      color={isActive ? '#A63FF5' : 'inherit'}
      px={5}
      h="44px"
      rounded="xl"
      transition="all 0.3s"
      _hover={{
        color: '#FF6A5F',
        bg: useColorModeValue('gray.100', 'gray.700'),
        transform: 'translateY(-2px)',
      }}
      onClick={onClick}
    >
      {children}
    </Button>
  );
};

const Header = () => {
  const navigate = useNavigate();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { language, changeLanguage } = useLanguage();
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const menuItemBg = useColorModeValue('purple.50', 'purple.900');

  const navItems = [
    { label: language === 'ko' ? '홈' : 'Home', path: '/' },
    { label: language === 'ko' ? '소개' : 'About', path: '/about' },
    { label: language === 'ko' ? '제품' : 'Products', path: '/products' },
    { label: language === 'ko' ? '문의' : 'Contact', path: '/contact' },
  ];

  const handleNavClick = (path) => {
    navigate(path);
    onClose();
  };

  const languages = [
    { code: 'ko', label: '한국어', flag: '🇰🇷' },
    { code: 'en', label: 'English', flag: '🇺🇸' },
  ];

  const currentLanguage = languages.find(lang => lang.code === language) || languages[0];

  return (
    <Box
      bg={bgColor}
      borderBottom="1px"
      borderColor={borderColor}
      position="sticky"
      top={0}
      zIndex={1000}
      shadow="md"
      backdropFilter="blur(10px)"
      backgroundColor={useColorModeValue('rgba(255, 255, 255, 0.95)', 'rgba(26, 32, 44, 0.95)')}
    >
      <Container maxW="container.xl">
        <Flex h={20} alignItems="center" justifyContent="space-between">
          {/* Logo */}
          <Image
            src="/aidu-logo.svg"
            alt="AIDU Logo"
            h="60px"
            cursor="pointer"
            onClick={() => navigate('/')}
            transition="all 0.3s"
            _hover={{ transform: 'scale(1.05)' }}
          />

          {/* Desktop Navigation */}
          <Flex display={{ base: 'none', md: 'flex' }} alignItems="center" gap={4}>
            <Stack direction="row" spacing={4}>
              {navItems.map((item) => (
                <NavLink
                  key={item.path}
                  to={item.path}
                  onClick={() => handleNavClick(item.path)}
                >
                  {item.label}
                </NavLink>
              ))}
            </Stack>

            {/* Language Switcher */}
            <Menu>
              <MenuButton
                as={Button}
                rightIcon={<ChevronDownIcon />}
                variant="ghost"
                fontSize="md"
                fontWeight="medium"
                h="44px"
                px={4}
                rounded="xl"
                _hover={{
                  bg: useColorModeValue('gray.100', 'gray.700'),
                }}
              >
                {currentLanguage.flag} {currentLanguage.label}
              </MenuButton>
              <MenuList rounded="xl" shadow="xl" border="1px" borderColor={borderColor}>
                {languages.map((lang) => (
                  <MenuItem
                    key={lang.code}
                    onClick={() => changeLanguage(lang.code)}
                    bg={language === lang.code ? menuItemBg : 'transparent'}
                    rounded="lg"
                    _hover={{ bg: menuItemBg }}
                  >
                    {lang.flag} {lang.label}
                  </MenuItem>
                ))}
              </MenuList>
            </Menu>
          </Flex>

          {/* CTA Button */}
          <Button
            size="md"
            h="48px"
            px={8}
            display={{ base: 'none', md: 'inline-flex' }}
            bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
            color="white"
            rounded="full"
            fontSize="md"
            fontWeight="bold"
            _hover={{
              bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
              transform: 'translateY(-2px)',
              shadow: 'xl',
            }}
            transition="all 0.3s"
            onClick={() => navigate('/contact')}
          >
            {language === 'ko' ? '시작하기' : 'Get Started'}
          </Button>

          {/* Mobile Menu Button */}
          <IconButton
            icon={<HamburgerIcon />}
            variant="ghost"
            display={{ base: 'flex', md: 'none' }}
            onClick={onOpen}
            aria-label="Open menu"
          />
        </Flex>
      </Container>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="right" onClose={onClose}>
        <DrawerOverlay />
        <DrawerContent>
          <DrawerCloseButton />
          <DrawerHeader borderBottomWidth="1px">
            <Image
              src="/aidu-logo.svg"
              alt="AIDU Logo"
              h="30px"
            />
          </DrawerHeader>

          <DrawerBody>
            <VStack spacing={4} align="stretch" mt={4}>
              {navItems.map((item) => (
                <Button
                  key={item.path}
                  variant="ghost"
                  justifyContent="flex-start"
                  fontSize="lg"
                  onClick={() => handleNavClick(item.path)}
                >
                  {item.label}
                </Button>
              ))}
              
              {/* Language Switcher for Mobile */}
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={<ChevronDownIcon />}
                  variant="ghost"
                  justifyContent="flex-start"
                  fontSize="lg"
                  w="full"
                >
                  {currentLanguage.flag} {currentLanguage.label}
                </MenuButton>
                <MenuList>
                  {languages.map((lang) => (
                    <MenuItem
                      key={lang.code}
                      onClick={() => changeLanguage(lang.code)}
                      bg={language === lang.code ? menuItemBg : 'transparent'}
                    >
                      {lang.flag} {lang.label}
                    </MenuItem>
                  ))}
                </MenuList>
              </Menu>

              <Button
                size="lg"
                mt={4}
                bgGradient="linear(to-r, #FF6A5F, #A63FF5)"
                color="white"
                _hover={{
                  bgGradient: "linear(to-r, #FF8FA5, #C56CFF)",
                }}
                onClick={() => {
                  navigate('/contact');
                  onClose();
                }}
              >
                {language === 'ko' ? '시작하기' : 'Get Started'}
              </Button>
            </VStack>
          </DrawerBody>
        </DrawerContent>
      </Drawer>
    </Box>
  );
};

export default Header;

